import { Alert, AlertTitle, Box, Divider, Typography } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import type { BulkLocationEditPreview } from '../../types/BulkLocationEdit';
import { rowHasChanges } from '../../types/BulkLocationEdit';
import { fieldDisplayLabel, formatFieldValue } from './editableFields';

interface AssistantChangePreviewProps {
  preview: BulkLocationEditPreview;
}

/** Empty values are easy to miss in a diff, so mark them explicitly. */
const renderValue = (value: string | null) => {
  if (value === null || value === '') {
    return (
      <Typography component="span" variant="caption" sx={{ color: '#8299A5', fontStyle: 'italic' }}>
        empty
      </Typography>
    );
  }
  return (
    <Typography component="span" variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  );
};

/**
 * Renders the server-computed dry run inside the chat panel.
 *
 * Descriptions are long prose, so old/new are stacked rather than placed in
 * side-by-side table columns the way the wider bulk edit dialog does.
 */
const AssistantChangePreview = ({ preview }: AssistantChangePreviewProps) => {
  const rowsWithChanges = preview.rows.filter(rowHasChanges);
  const rowsWithErrors = preview.rows.filter((row) => row.errors.length > 0);

  return (
    <Box sx={{ mt: 1 }}>
      {preview.fileErrors.map((issue, index) => (
        <Alert severity="error" sx={{ mb: 1 }} key={`file-${index}`}>
          <Typography variant="caption">{issue.message}</Typography>
        </Alert>
      ))}

      {preview.warnings.map((warning, index) => (
        <Alert severity="warning" sx={{ mb: 1 }} key={`warn-${index}`}>
          <Typography variant="caption">{warning}</Typography>
        </Alert>
      ))}

      {rowsWithErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <AlertTitle sx={{ fontSize: '0.8rem' }}>Problems found</AlertTitle>
          {rowsWithErrors.flatMap((row) =>
            row.errors.map((issue, index) => (
              <Typography variant="caption" component="div" key={`${row.row}-${index}`}>
                {row.locationName ? `${row.locationName}: ` : `Row ${issue.row}: `}
                {issue.message}
              </Typography>
            )),
          )}
        </Alert>
      )}

      {rowsWithChanges.map((row) => (
        <Box
          key={row.row}
          sx={{ border: '1px solid #E3E8F0', borderRadius: 2, p: 1.25, mb: 1, backgroundColor: '#fff' }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            {row.locationName}
          </Typography>

          {row.changes.map((change) => (
            <Box key={change.field} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#5E7782', display: 'block', mb: 0.5 }}>
                {fieldDisplayLabel(change.field)}
              </Typography>
              <Box sx={{ opacity: 0.7 }}>
                {renderValue(formatFieldValue(change.field, change.oldValue))}
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <ArrowRightAltIcon fontSize="small" sx={{ color: '#8299A5', flexShrink: 0 }} />
                {renderValue(formatFieldValue(change.field, change.newValue))}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default AssistantChangePreview;
